import React from 'react';

import { GenerationSettings, TopicSuggestion } from '../types/index.ts';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { ensurePfReady } from '../services/ensurePfReady.ts';

interface TemplateAndSettingsProps {
  selectedTopics: TopicSuggestion[];
  settings: GenerationSettings;
  onChangeSettings: (next: GenerationSettings) => void;
  onBack: () => void;
  onGenerate: (args: { topics: TopicSuggestion[]; settings: GenerationSettings }) => void | Promise<void>;
  isLoading?: boolean;
}

export const TemplateAndSettings: React.FC<TemplateAndSettingsProps> = ({
  selectedTopics,
  settings,
  onChangeSettings,
  onBack,
  onGenerate,
  isLoading = false
}) => {
  const [progressStage, setProgressStage] = React.useState<'idle' | 'warming' | 'generating' | 'finalizing'>('idle');
  const [progressMessage, setProgressMessage] = React.useState<string | null>(null);

  const effectiveTopics = React.useMemo(() => {
    // Quickstart still uses the user's selected topics, but produces a single SIMPI.
    // The backend will merge/condense these topics into a short step list.
    return selectedTopics;
  }, [selectedTopics]);

  const isWorking = isLoading || progressStage !== 'idle';
  const canGenerate = selectedTopics.length > 0 && effectiveTopics.length > 0 && !isWorking;

  const steps: Array<{ key: typeof progressStage; label: string }> = [
    { key: 'warming', label: 'Starting AI processor (cold start)' },
    { key: 'generating', label: settings.template === 'quickstart' ? 'Generating SIMPI' : 'Generating SIMPIs' },
    { key: 'finalizing', label: 'Finalizing results' }
  ];
  const currentIdx = steps.findIndex((s) => s.key === progressStage);

  const handleGenerateClick = async () => {
    if (!canGenerate) return;
    try {
      setProgressStage('warming');
      setProgressMessage('Starting AI processor…');
      await ensurePfReady({
        maxWaitMs: 45000,
        onProgress: (msg) => setProgressMessage(msg)
      });
      setProgressStage('generating');
      setProgressMessage(null);
      await onGenerate({ topics: effectiveTopics, settings });
      // Typically unmounts immediately (navigates to results). Keep best-effort fallback.
      setProgressStage('finalizing');
      setProgressMessage('Finalizing…');
    } catch (e) {
      // Keep UX minimal; App-level error handling remains the source of truth.
      setProgressStage('idle');
      setProgressMessage(e instanceof Error ? e.message : null);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Generation settings</CardTitle>
        <CardDescription>
          Review the output type and adjust tone or language before generation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isWorking && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2 text-blue-900 font-medium">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-300 border-b-blue-700" />
              {progressStage === 'warming' ? 'Starting up…' : 'Working…'}
            </div>
            {progressMessage && <div className="mt-1 text-sm text-blue-800">{progressMessage}</div>}
            <ol className="mt-3 space-y-2 text-sm">
              {steps.map((s, idx) => {
                const isCurrent = idx === currentIdx;
                const isDone = currentIdx >= 0 && idx < currentIdx;
                return (
                  <li key={s.key} className="flex items-center gap-2">
                    <span
                      className={[
                        'inline-flex h-5 w-5 items-center justify-center rounded-full border text-xs',
                        isDone ? 'bg-green-50 border-green-300 text-green-700' : '',
                        isCurrent ? 'bg-blue-50 border-blue-300 text-blue-700' : '',
                        !isDone && !isCurrent ? 'bg-gray-50 border-gray-300 text-gray-600' : ''
                      ].join(' ')}
                    >
                      {isDone ? '✓' : idx + 1}
                    </span>
                    <span className={isCurrent ? 'font-medium text-gray-900' : 'text-gray-700'}>{s.label}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">Output type</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <button
              type="button"
              onClick={() => onChangeSettings({ ...settings, template: 'quickstart' })}
              className={[
                'rounded-lg border p-4 text-left transition-colors',
                settings.template === 'quickstart'
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-primary/50'
              ].join(' ')}
              disabled={isWorking}
            >
              <div className="font-semibold text-gray-900">Quickstart</div>
              <div className="mt-1 text-sm text-gray-600">
                One focused SIMPI for setup, first use, and key capabilities.
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChangeSettings({ ...settings, template: 'product_guide' })}
              className={[
                'rounded-lg border p-4 text-left transition-colors',
                settings.template === 'product_guide'
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-primary/50'
              ].join(' ')}
              disabled={isWorking}
            >
              <div className="font-semibold text-gray-900">Product Guide</div>
              <div className="mt-1 text-sm text-gray-600">
                Broader coverage across setup, usage, maintenance, and troubleshooting.
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Style</label>
            <select
              value={settings.style}
              onChange={(e) => onChangeSettings({ ...settings, style: e.target.value as GenerationSettings['style'] })}
              className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isWorking}
            >
              <option value="auto">Auto</option>
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="technical">Technical</option>
              <option value="concise">Concise</option>
              <option value="kids">For kids</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Guide language</label>
            <select
              value={settings.preferredLanguage}
              onChange={(e) =>
                onChangeSettings({ ...settings, preferredLanguage: e.target.value as GenerationSettings['preferredLanguage'] })
              }
              className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isWorking}
            >
              <option value="auto">Auto (from document)</option>
              <option value="en">English</option>
              <option value="de">German</option>
            </select>
            <p className="text-xs text-gray-500">
              Matches the source language chosen before topic extraction.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={onBack} disabled={isWorking}>
            Back
          </Button>
          <Button
            onClick={handleGenerateClick}
            disabled={!canGenerate}
          >
            {settings.template === 'quickstart' ? 'Generate SIMPI' : 'Generate SIMPIs'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


